import { pubsub } from '@services';

/**
 * Extract the numeric ID from a Shopify GID, e.g. gid://shopify/Metaobject/123
 * @param {String} gid - The GID
 * @returns {String} The numeric ID
 */
function parseGid(gid) {
  return String(gid ?? '')
    .split('/')
    .pop();
}

class StoreAvailabilityDialog extends HTMLElement {
  dialog = this.querySelector('dialog');
  storeElements = this.querySelectorAll('[data-store]');
  emptyMessage = this.querySelector('[data-store-availability-empty]');
  proxyUrl = this.dataset.proxyUrl;
  fetchedVariantId = null;
  abortController = null;

  connectedCallback() {
    // Refetch when a different variant is selected within the dialog
    this.addEventListener('change', (event) => {
      const { variantId } = event.target.dataset;
      if (variantId) this.fetchAvailability(variantId);
    });

    // Fetch the availability for the selected variant when the dialog opens
    this.observer = new MutationObserver(() => {
      if (this.dialog.open) this.fetchAvailability(this.selectedVariantId);
    });
    this.observer.observe(this.dialog, { attributeFilter: ['open'] });

    // Keep the dialog in sync with the main variant select
    this.unsubscribeVariantChange = pubsub.subscribe(
      `variant:changed:${this.dataset.productId}`,
      ({ variantId }) => this.onVariantChange(variantId)
    );
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.unsubscribeVariantChange?.();
  }

  /**
   * The variant ID of the checked option within the dialog
   * @returns {String|undefined} The variant ID
   */
  get selectedVariantId() {
    return this.querySelector('input[data-variant-id]:checked')?.dataset.variantId;
  }

  /**
   * Handle a variant change on the main product form
   * @param {String} variantId - The variant ID
   */
  onVariantChange(variantId) {
    const input = this.querySelector(`input[data-variant-id="${variantId}"]`);
    if (input) input.checked = true;

    if (this.dialog.open) {
      this.fetchAvailability(variantId);
    } else {
      // Force a refetch the next time the dialog opens
      this.fetchedVariantId = null;
    }
  }

  /**
   * Fetch the store availability from the app proxy
   * @param {String} variantId - The variant ID
   */
  async fetchAvailability(variantId) {
    if (!variantId || variantId === this.fetchedVariantId) return;

    this.abortController?.abort();
    const abortController = new AbortController();
    this.abortController = abortController;
    this.dataset.loading = 'true';

    try {
      const response = await fetch(`${this.proxyUrl}?variant_id=${variantId}`, {
        signal: abortController.signal,
      });
      if (!response.ok)
        throw new Error(`Store availability request failed with status ${response.status}`);

      const { stores } = await response.json();
      this.renderAvailability(stores);
      this.fetchedVariantId = variantId;
    } catch (error) {
      if (error.name === 'AbortError') return;

      console.error(error);
      this.renderAvailability([]);
    } finally {
      // Only reset the loading state when no newer request has been started
      if (this.abortController === abortController) {
        this.abortController = null;
        this.dataset.loading = 'false';
      }
    }
  }

  /**
   * Show the stores returned by the proxy and toggle their stock labels
   * @param {Array} stores - The stores with availability for the current variant
   */
  renderAvailability(stores = []) {
    let visibleStores = 0;

    this.storeElements.forEach((storeElement) => {
      const { metaobjectId } = storeElement.dataset;
      const store =
        metaobjectId &&
        stores.find((item) => parseGid(item.metaobjectGid) === parseGid(metaobjectId));

      storeElement.hidden = !store;
      if (!store) return;

      visibleStores += 1;
      storeElement.querySelector('[data-store-in-stock]').hidden = !store.inStock;
      storeElement.querySelector('[data-store-out-of-stock]').hidden = store.inStock;
    });

    this.emptyMessage.hidden = visibleStores > 0;
  }
}

customElements.define('store-availability-dialog', StoreAvailabilityDialog);
